import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  EstadoVazio,
  Modal,
  Selo,
  Tabela,
  useDialogo,
  type ColunaTabela,
} from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';
import { formatarCompetencia } from '../servicos/formato';
import { ErroDaApi } from './ErroDaApi';

/** Raiz das pastas de cliente no Drive da MS3 — espelho de `RAIZ_CLIENTES_MS3` no domínio. */
const RAIZ_CLIENTES_MS3 = '/Negócios/MS3 Capital/Clientes';

interface EmpresaParaPasta {
  readonly legalName: string;
  readonly taxpayerId: string;
}

interface PastaDaEmpresa {
  readonly businessId: string;
  readonly caminho: string;
  readonly url: string | null;
}

interface SpedNoDrive {
  readonly id: string;
  readonly competencia: string;
  readonly caminhoEsperado: string;
  readonly url: string | null;
  readonly noDrive: boolean;
}

/**
 * RN-343 a RN-347 — A pasta da empresa no Drive da MS3.
 *
 * ## O que esta tela controla
 *
 * Onde a equipe guarda — e busca — o documento fiscal daquele cliente. Não é
 * integração de leitura: o sistema não lista o conteúdo do Drive. Ele guarda o
 * **caminho combinado** e diz quais SPEDs ainda não foram arquivados lá.
 *
 * ## "Vincular pasta", como no Deca 1
 *
 * Um botão, um modal (lista de 15/09, item 14): o nome da pasta já vem
 * preenchido com "Razão social - 4 últimos dígitos do CNPJ", o destino é o
 * pré-determinado da MS3 (`/Negócios/MS3 Capital/Clientes/<nome>`) e "Criar
 * estrutura de diretórios" chama o fluxo do n8n que monta as pastas lá. Sem
 * webhook configurado, isso recusa em vez de gravar um caminho que ninguém
 * criou (RN-344). "Vincular pasta existente" fica no mesmo modal, para quando
 * a pasta foi feita à mão; com pasta vinculada, o modal mostra o caminho e
 * deixa editá-lo.
 *
 * ## Desvincular não apaga nada
 *
 * O legado não tinha como desfazer: um caminho errado só saía por SQL. Aqui
 * desvincular só para de afirmar que a pasta é daquela empresa — o Drive não é
 * tocado, e a tela diz isso porque a expectativa contrária custaria documento.
 *
 * ## É uma seção do Cadastro, não uma aba
 *
 * A pasta no Drive é dado cadastral da empresa, como o endereço e a conta
 * bancária: é onde ela está, não o que ela opera. Por isso vive como cartão
 * dentro da aba Cadastro, ao lado dos documentos.
 */
export function DriveDaEmpresa(props: { readonly businessId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { confirmar } = useDialogo();
  const [vinculando, setVinculando] = useState(false);
  const [anotando, setAnotando] = useState<SpedNoDrive | null>(null);
  const [soPendentes, setSoPendentes] = useState(false);

  const pasta = useQuery({
    queryKey: ['drive-pasta', props.businessId],
    queryFn: () =>
      requisicaoAutenticada<PastaDaEmpresa | null>(`/business/${props.businessId}/drive/pasta`),
  });
  const empresa = useQuery({
    queryKey: ['empresa', props.businessId],
    queryFn: () => requisicaoAutenticada<EmpresaParaPasta>(`/business/${props.businessId}`),
  });

  const speds = useQuery({
    queryKey: ['drive-speds', props.businessId, soPendentes],
    queryFn: () =>
      requisicaoAutenticada<SpedNoDrive[]>(
        `/business/${props.businessId}/drive/speds${soPendentes ? '/pendentes' : ''}`,
      ),
  });

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['drive-speds', props.businessId] });
    void queryClient.invalidateQueries({ queryKey: ['drive-pasta', props.businessId] });
    void queryClient.invalidateQueries({ queryKey: ['empresa', props.businessId] });
  };

  const desvincular = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<void>(`/business/${props.businessId}/drive/vinculo`, {
        metodo: 'DELETE',
      }),
    onSuccess: invalidar,
  });

  const lista = speds.data ?? [];
  const pendentes = lista.filter((s) => !s.noDrive).length;

  // "Sem pasta vinculada" é estado normal de quem ainda não vinculou, não erro.
  // A API responde 400 com essa mensagem, e mostrá-la como cartão vermelho faria
  // toda empresa nova parecer quebrada.
  //
  // A fonte é `/speds`, que erra nesse caso. `/speds/pendentes` devolve `[]` de
  // propósito (RN-347: sem pasta não há o que arquivar, e listar seria oferecer
  // trabalho impossível) — por isso o filtro de pendentes só aparece depois de
  // haver pasta.
  const semPasta =
    speds.isError && /não tem pasta vinculada|pasta vinculada/i.test(speds.error.message);

  const colunas: readonly ColunaTabela<SpedNoDrive>[] = [
    {
      chave: 'competencia',
      titulo: 'Competência',
      larguraMinima: '9rem',
      celula: (s) => formatarCompetencia(s.competencia),
    },
    {
      chave: 'caminhoEsperado',
      titulo: 'Caminho esperado',
      larguraMinima: '22rem',
      celula: (s) => (
        <span className="valor-fiscal text-xs text-conteudo-secundario">{s.caminhoEsperado}</span>
      ),
    },
    {
      chave: 'noDrive',
      titulo: 'Arquivado',
      larguraMinima: '9rem',
      celula: (s) => (
        <Selo tom={s.noDrive ? 'positivo' : 'atencao'}>{s.noDrive ? 'Sim' : 'Pendente'}</Selo>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '11rem',
      celula: (s) => (
        <span className="flex flex-wrap justify-end gap-2">
          {s.url !== null && (
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sm text-acento-ciano underline-offset-4 hover:underline"
            >
              Abrir
            </a>
          )}
          <Botao variante="sutil" tamanho="pequeno" onClick={() => setAnotando(s)}>
            {s.noDrive ? 'Trocar link' : 'Anotar link'}
          </Botao>
        </span>
      ),
    },
  ];

  return (
    <Cartao
      como="section"
      titulo="Drive"
      subtitulo="Onde ficam os arquivos desta empresa no Drive da MS3. O sistema guarda o caminho combinado e aponta o que ainda não foi arquivado — ele não lê o conteúdo do Drive."
      acao={
        <Botao tamanho="pequeno" onClick={() => setVinculando(true)}>
          Vincular pasta
        </Botao>
      }
    >
      <div className="flex flex-col gap-4">
        {pasta.data !== null && pasta.data !== undefined && (
          <p className="text-sm text-conteudo-secundario">
            Pasta vinculada:{' '}
            <span className="valor-fiscal text-xs text-conteudo-primario">
              {pasta.data.caminho}
            </span>
          </p>
        )}
        {desvincular.isError && (
          <ErroDaApi titulo="Não foi possível desvincular" erro={desvincular.error} />
        )}

        {!semPasta && (
          <div className="flex flex-wrap items-center gap-3 rounded-card border border-borda px-4 py-3">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={soPendentes}
                onChange={(e) => setSoPendentes(e.target.checked)}
                className="h-4 w-4 rounded border-borda bg-superficie-base text-acento-ciano"
              />
              <span className="text-sm text-conteudo-primario">Só o que falta arquivar</span>
            </label>
            {!soPendentes && pendentes > 0 && (
              <span className="text-xs text-conteudo-sutil">
                {pendentes} de {lista.length} ainda não foram para o Drive
              </span>
            )}
            <Botao
              variante="sutil"
              tamanho="pequeno"
              className="ml-auto"
              carregando={desvincular.isPending}
              onClick={async () => {
                // Tom padrão de propósito: desvincular não destrói nada no Drive, e o
                // botão vermelho faria parecer que sim.
                const ok = await confirmar({
                  titulo: 'Desvincular a pasta desta empresa?',
                  descricao:
                    'Nada é apagado no Drive — o sistema apenas deixa de apontar para lá. Dá para vincular de novo depois.',
                  rotuloConfirmar: 'Desvincular',
                });
                if (ok) desvincular.mutate();
              }}
            >
              Desvincular pasta
            </Botao>
          </div>
        )}

        {speds.isError && !semPasta && (
          <ErroDaApi titulo="Não foi possível carregar" erro={speds.error} />
        )}

        {/* Sem pasta, uma linha basta: o "Vincular pasta" do cabeçalho já é o
            caminho, e um segundo botão igual no meio do cartão só repetia. */}
        {semPasta ? (
          <p className="text-sm text-conteudo-sutil">Nenhuma pasta vinculada ainda.</p>
        ) : !speds.isPending && lista.length === 0 ? (
          <EstadoVazio
            titulo={soPendentes ? 'Nada pendente' : 'Nenhum SPED'}
            descricao={
              soPendentes
                ? 'Todos os arquivos desta empresa já estão no Drive.'
                : 'Esta empresa ainda não tem SPED processado — não há o que arquivar.'
            }
          />
        ) : (
          <Tabela
            legenda="SPEDs e o arquivamento no Drive"
            colunas={colunas}
            dados={lista}
            chaveDaLinha={(s) => s.id}
            chaveDeCabecalhoDaLinha="competencia"
            carregando={speds.isPending}
          />
        )}

        <ModalDeVinculo
          key={`${pasta.data?.caminho ?? ''}|${empresa.data?.taxpayerId ?? ''}`}
          businessId={props.businessId}
          empresa={empresa.data ?? null}
          pastaAtual={pasta.data ?? null}
          aberto={vinculando}
          aoFechar={() => setVinculando(false)}
          aoVincular={invalidar}
        />

        <ModalDeUrl
          businessId={props.businessId}
          sped={anotando}
          aoFechar={() => setAnotando(null)}
          aoAnotar={invalidar}
        />
      </div>
    </Cartao>
  );
}

/* ─── A pasta, fora do cadastro ───────────────────────────────────────────── */

/**
 * Só a pasta e o "Vincular pasta" — para o card do CRM, onde a lista de SPEDs
 * não cabe. O mesmo modal e as mesmas rotas do cartão Drive acima: vincular
 * aqui aparece lá, e vice-versa (as consultas são as mesmas).
 */
export function PastaDoDrive(props: { readonly businessId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [vinculando, setVinculando] = useState(false);

  const pasta = useQuery({
    queryKey: ['drive-pasta', props.businessId],
    queryFn: () =>
      requisicaoAutenticada<PastaDaEmpresa | null>(`/business/${props.businessId}/drive/pasta`),
  });
  const empresa = useQuery({
    queryKey: ['empresa', props.businessId],
    queryFn: () => requisicaoAutenticada<EmpresaParaPasta>(`/business/${props.businessId}`),
  });

  const invalidar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['drive-speds', props.businessId] });
    void queryClient.invalidateQueries({ queryKey: ['drive-pasta', props.businessId] });
    void queryClient.invalidateQueries({ queryKey: ['empresa', props.businessId] });
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda px-3 py-2.5">
      <span className="min-w-0 text-sm text-conteudo-secundario">
        {pasta.isError ? (
          'Não foi possível consultar a pasta.'
        ) : pasta.data === null || pasta.data === undefined ? (
          pasta.isPending ? (
            'Consultando a pasta…'
          ) : (
            'Nenhuma pasta vinculada no Drive.'
          )
        ) : (
          <>
            Pasta no Drive:{' '}
            <span className="valor-fiscal break-all text-xs text-conteudo-primario">
              {pasta.data.caminho}
            </span>
          </>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-3">
        {pasta.data?.url !== null && pasta.data?.url !== undefined && (
          <a
            href={pasta.data.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-sm text-acento-ciano underline-offset-4 hover:underline"
          >
            Abrir
          </a>
        )}
        <Botao variante="secundario" tamanho="pequeno" onClick={() => setVinculando(true)}>
          Vincular pasta
        </Botao>
      </span>
      <ModalDeVinculo
        key={`${pasta.data?.caminho ?? ''}|${empresa.data?.taxpayerId ?? ''}`}
        businessId={props.businessId}
        empresa={empresa.data ?? null}
        pastaAtual={pasta.data ?? null}
        aberto={vinculando}
        aoFechar={() => setVinculando(false)}
        aoVincular={invalidar}
      />
    </div>
  );
}

/* ─── Vincular pasta ──────────────────────────────────────────────────────── */

/**
 * O modal "Vincular pasta" do Deca 1 (`VincularPastaModal.js`).
 *
 * Sem pasta: nome pré-preenchido, destino pré-determinado e "Criar estrutura
 * de diretórios" (n8n). "Vincular pasta existente" troca o campo para o
 * caminho completo. Com pasta: mostra o caminho e "Editar" abre o campo para
 * repontar.
 */
function ModalDeVinculo(props: {
  readonly businessId: string;
  readonly empresa: EmpresaParaPasta | null;
  readonly pastaAtual: PastaDaEmpresa | null;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  readonly aoVincular: () => void;
}): JSX.Element {
  const nomeSugerido =
    props.empresa === null
      ? ''
      : `${props.empresa.legalName.trim()} - ${props.empresa.taxpayerId.slice(-4)}`;
  const [modo, setModo] = useState<'criar' | 'existente' | 'editar'>(
    props.pastaAtual === null ? 'criar' : 'editar',
  );
  const [nome, setNome] = useState(nomeSugerido);
  const [caminho, setCaminho] = useState(props.pastaAtual?.caminho ?? '');
  const [editando, setEditando] = useState(false);

  const criar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<PastaDaEmpresa>(`/business/${props.businessId}/drive/estrutura`, {
        metodo: 'POST',
        corpo: { nomeDaPasta: nome.trim() },
      }),
    onSuccess: props.aoVincular,
  });
  const vincular = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<PastaDaEmpresa>(`/business/${props.businessId}/drive/vinculo`, {
        metodo: 'POST',
        corpo: { caminho: caminho.trim() },
      }),
    onSuccess: props.aoVincular,
  });

  const concluido = criar.isSuccess || vincular.isSuccess;
  const ocupado = criar.isPending || vincular.isPending;
  const destino = `${RAIZ_CLIENTES_MS3}/${nome.trim().replace(/^\/+|\/+$/g, '') || '…'}`;

  const fechar = (): void => {
    criar.reset();
    vincular.reset();
    setEditando(false);
    props.aoFechar();
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Vincular pasta"
      descricao={
        props.empresa === null
          ? undefined
          : `Vincular ${props.empresa.legalName} a uma pasta no Drive da MS3.`
      }
      largura="larga"
      rodape={
        concluido ? (
          <Botao onClick={fechar}>Finalizar</Botao>
        ) : (
          <>
            <Botao variante="secundario" onClick={fechar} disabled={ocupado}>
              Cancelar
            </Botao>
            {modo === 'criar' && (
              <Botao
                onClick={() => criar.mutate()}
                disabled={nome.trim() === ''}
                carregando={criar.isPending}
              >
                Criar estrutura de diretórios
              </Botao>
            )}
            {modo === 'existente' && (
              <Botao
                onClick={() => vincular.mutate()}
                disabled={caminho.trim() === ''}
                carregando={vincular.isPending}
              >
                Vincular pasta existente
              </Botao>
            )}
            {modo === 'editar' && editando && (
              <Botao
                onClick={() => vincular.mutate()}
                disabled={caminho.trim() === '' || caminho.trim() === props.pastaAtual?.caminho}
                carregando={vincular.isPending}
              >
                Atualizar caminho da pasta
              </Botao>
            )}
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {concluido ? (
          <Aviso tom="positivo" titulo={criar.isSuccess ? 'Estrutura criada' : 'Pasta vinculada'}>
            Caminho:{' '}
            <span className="valor-fiscal text-xs">
              {criar.data?.caminho ?? vincular.data?.caminho}
            </span>
          </Aviso>
        ) : modo === 'editar' ? (
          <>
            <Aviso
              tom="positivo"
              titulo="Pasta já vinculada"
              acao={
                editando ? undefined : (
                  <Botao variante="secundario" tamanho="pequeno" onClick={() => setEditando(true)}>
                    Editar
                  </Botao>
                )
              }
            >
              Caminho completo:{' '}
              <span className="valor-fiscal text-xs">{props.pastaAtual?.caminho}</span>
            </Aviso>
            {editando && (
              <Campo
                rotulo="Novo caminho da pasta"
                required
                autoFocus
                placeholder={`Ex.: ${RAIZ_CLIENTES_MS3}/Nova Empresa`}
                ajuda="Digite o caminho completo da pasta no Drive da MS3. Nada é movido — o sistema só passa a apontar para lá."
                value={caminho}
                onChange={(e) => setCaminho(e.target.value)}
              />
            )}
          </>
        ) : modo === 'criar' ? (
          <>
            <Campo
              rotulo="Nome da pasta"
              required
              autoFocus
              placeholder="Ex.: Empresa ABC Ltda - 0001"
              ajuda="Esse será o nome da pasta no Drive da MS3."
              value={nome}
              onChange={(e) => setNome(e.target.value)}
            />
            <p className="text-sm text-conteudo-secundario">
              Destino:{' '}
              <span className="valor-fiscal text-xs text-conteudo-primario">{destino}</span>
            </p>
            <Aviso tom="informacao">
              <strong>Criar estrutura</strong> monta, pelo n8n, a pasta e as subpastas no modelo da
              MS3 e vincula o caminho aqui. Se a pasta já existe no Drive,{' '}
              <button
                type="button"
                className="underline underline-offset-4 hover:text-conteudo-primario"
                onClick={() => setModo('existente')}
              >
                vincule a pasta existente
              </button>
              .
            </Aviso>
          </>
        ) : (
          <>
            <Campo
              rotulo="Caminho no Drive"
              required
              autoFocus
              placeholder={`Ex.: ${RAIZ_CLIENTES_MS3}/Empresa ABC Ltda - 0001`}
              ajuda="O caminho completo da pasta que já existe. Use quando ela foi criada à mão."
              value={caminho}
              onChange={(e) => setCaminho(e.target.value)}
            />
            <Aviso tom="atencao">
              Repontar a pasta redireciona para onde a equipe busca — e deposita — documento fiscal
              desta empresa. Confira o caminho antes de gravar.{' '}
              <button
                type="button"
                className="underline underline-offset-4 hover:text-conteudo-primario"
                onClick={() => setModo('criar')}
              >
                Prefiro criar a estrutura
              </button>
              .
            </Aviso>
          </>
        )}

        {criar.isError && <ErroDaApi titulo="A estrutura não foi criada" erro={criar.error} />}
        {vincular.isError && <ErroDaApi titulo="Não foi possível vincular" erro={vincular.error} />}
      </div>
    </Modal>
  );
}

/* ─── Anotar URL do SPED ──────────────────────────────────────────────────── */

function ModalDeUrl(props: {
  readonly businessId: string;
  readonly sped: SpedNoDrive | null;
  readonly aoFechar: () => void;
  readonly aoAnotar: () => void;
}): JSX.Element {
  const [url, setUrl] = useState('');

  const anotar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<SpedNoDrive>(
        `/business/${props.businessId}/drive/speds/${props.sped?.id ?? ''}/url`,
        { metodo: 'PATCH', corpo: { url } },
      ),
    onSuccess: () => {
      props.aoAnotar();
      fechar();
    },
  });

  const fechar = (): void => {
    anotar.reset();
    setUrl('');
    props.aoFechar();
  };

  return (
    <Modal
      key={props.sped?.id ?? 'nenhum'}
      aberto={props.sped !== null}
      aoFechar={fechar}
      titulo="Anotar link do arquivo"
      descricao={
        props.sped === null
          ? undefined
          : `Competência ${formatarCompetencia(props.sped.competencia)}`
      }
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => anotar.mutate()}
            disabled={url.trim() === ''}
            carregando={anotar.isPending}
          >
            Anotar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {props.sped !== null && (
          <p className="text-sm text-conteudo-secundario">
            O caminho esperado é{' '}
            <span className="valor-fiscal text-xs">{props.sped.caminhoEsperado}</span>.
          </p>
        )}

        <Campo
          rotulo="URL do arquivo no Drive"
          type="url"
          required
          ajuda="Cole o link do arquivo já arquivado. O sistema não sobe nada — ele anota onde está."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />

        {anotar.isError && <ErroDaApi titulo="Não foi possível anotar" erro={anotar.error} />}
      </div>
    </Modal>
  );
}
